const PHOTO_STITCH_NOTES_PATTERN = /[^\w\s.,!?@$&£'"\-_()\n]/gu;

const sanitizePhotoStitchNotes = (text) => text.replace(PHOTO_STITCH_NOTES_PATTERN, '');

class ProductPhotoStitch extends HTMLElement {
  #dockObserver = null;
  #abortController = null;
  #initialized = false;
  #isOpening = false;
  #lightboxJustClosed = false;
  /** @type {Element | null} */
  #modalReturnFocus = null;
  /** @type {Element | null} */
  #lightboxReturnFocus = null;
  /** @type {string | null} */
  #selectedCorner = null;
  /** @type {Map<string, { file: File, url: string, color: string }>} */
  #uploads = new Map();

  connectedCallback() {
    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (this.modal?.hasAttribute('open')) {
      this.close();
    }

    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#dockObserver) {
      this.#dockObserver.disconnect();
      this.#dockObserver = null;
    }

    this.#revokeAllObjectUrls();
    this.#uploads.clear();
    this.#selectedCorner = null;
    this.#modalReturnFocus = null;
    this.#lightboxReturnFocus = null;
    this.#initialized = false;
    this.isActive = false;
  }

  #setup() {
    if (this.#initialized) return;

    this.container = this.querySelector('.photo-stitch-trigger');
    this.modal = this.querySelector('.photo-stitch-modal');
    this.modalSheet = this.querySelector('.photo-stitch-modal__content');
    this.saveBtn = this.querySelector('.js-ps-save');
    this.removeBtn = this.querySelector('.js-ps-remove');
    this.notesInput = this.querySelector('.js-ps-notes');
    this.errorEl = this.querySelector('.js-ps-error');
    this.statusText = this.querySelector('.js-ps-status-text');
    this.colorLabel = this.querySelector('.js-ps-label-color');
    this.colorGroup = this.querySelector('.js-ps-color-group');
    this.colorHeading = this.querySelector('.js-ps-color-heading');
    this.lightbox = this.querySelector('.photo-stitch-lightbox');
    this.lightboxImg = this.querySelector('.js-ps-lightbox-img');

    const configScript = this.querySelector(':scope > script[type="application/json"]');

    if (!configScript) {
      console.error('[product-photo-stitch] Missing config JSON');
      return;
    }

    try {
      this.config = JSON.parse(configScript.textContent);
    } catch (error) {
      console.error('[product-photo-stitch] Invalid config JSON', error);
      return;
    }

    if (!this.container || !this.modal || !this.saveBtn) {
      console.error('[product-photo-stitch] Missing required markup');
      return;
    }

    this.cornerByKey = new Map();

    for (const corner of this.config.corners ?? []) {
      this.cornerByKey.set(corner.key, corner);
    }

    this.isActive = false;
    this.#selectedCorner = null;
    this.#modalReturnFocus = null;
    this.#lightboxReturnFocus = null;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#bindEvents(signal);
    this.#syncColorUi();
    this.#updateSaveLabel();
    this.validate();
    this.#initialized = true;
  }

  #bindEvents(signal) {
    this.editBtns = this.querySelectorAll('.js-ps-edit');

    for (const btn of this.editBtns) {
      btn.addEventListener('click', () => this.open(), { signal });
    }

    for (const el of this.querySelectorAll('.js-ps-close')) {
      el.addEventListener('click', () => this.close(), { signal });
    }

    for (const btn of this.querySelectorAll('.js-ps-example-zoom')) {
      btn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.#openLightbox(btn.dataset.fullSrc, btn.dataset.alt);
        },
        { signal },
      );
    }

    for (const el of this.querySelectorAll('.js-ps-lightbox-close')) {
      el.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#closeLightbox();
        },
        { signal },
      );
    }

    this.lightbox?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
      },
      { signal },
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        if (!this.lightbox?.hasAttribute('open')) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.#closeLightbox();
      },
      { signal, capture: true },
    );

    document.addEventListener(
      'keyup',
      (event) => {
        if (event.key !== 'Escape' && event.code !== 'Escape') return;
        if (!this.lightbox?.hasAttribute('open') && !this.#lightboxJustClosed) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.#lightboxJustClosed = false;
      },
      { signal, capture: true },
    );

    this.saveBtn.addEventListener('click', () => this.save(), { signal });

    if (this.removeBtn) {
      this.removeBtn.addEventListener('click', () => this.reset(), { signal });
    }

    for (const btn of this.querySelectorAll('.js-ps-corner')) {
      btn.addEventListener(
        'click',
        () => {
          const key = btn.dataset.corner;
          if (!key) return;

          const hasUpload = this.#uploads.has(key);

          if (!hasUpload) {
            const input = this.querySelector(`.js-ps-file[data-corner="${key}"]`);
            input?.click();
            return;
          }

          // Second tap on the already-selected corner replaces the photo.
          if (this.#selectedCorner === key) {
            const input = this.querySelector(`.js-ps-file[data-corner="${key}"]`);
            input?.click();
            return;
          }

          this.#selectCorner(key);
        },
        { signal },
      );
    }

    for (const input of this.querySelectorAll('.js-ps-file')) {
      input.addEventListener('change', () => this.#onFileChange(input), { signal });
    }

    for (const clearBtn of this.querySelectorAll('.js-ps-clear')) {
      clearBtn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#clearCorner(clearBtn.dataset.corner);
        },
        { signal },
      );
    }

    for (const input of this.querySelectorAll('[name="photo-stitch-color"]')) {
      input.addEventListener(
        'change',
        () => {
          this.#applySelectedColor(input.value);
        },
        { signal },
      );
    }

    if (this.notesInput) {
      this.notesInput.addEventListener(
        'input',
        () => {
          this.#sanitizeNotesInput();
        },
        { signal },
      );
    }

    if (this.modalSheet) {
      this.#dockObserver = new MutationObserver(() => {
        if (!this.#useDock()) return;

        if (!this.modalSheet.classList.contains('active') && this.modal.hasAttribute('open')) {
          this.#syncDockDismissed();
        }
      });
      this.#dockObserver.observe(this.modalSheet, { attributes: true, attributeFilter: ['class'] });
    }
  }

  open() {
    if (!this.#initialized || !this.modal) return;

    const useDock = this.#useDock() && this.modalSheet;
    this.#isOpening = true;
    this.#modalReturnFocus = document.activeElement instanceof Element ? document.activeElement : null;

    if (useDock) {
      if (window.dockManager) {
        window.dockManager.closeAll();
        window.dockManager.open(this.modalSheet);
      } else {
        this.modalSheet.classList.add('active');
        document.body.classList.add('dock-open');
      }

      this.modalSheet.style.transform = '';
    }

    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.#isOpening = false;

    requestAnimationFrame(() => {
      this.querySelector('.photo-stitch-modal__close')?.focus();
    });
  }

  close() {
    this.#closeLightbox();

    if (this.modalSheet && this.modalSheet.classList.contains('active')) {
      this.modalSheet.classList.remove('active');

      if (window.dockManager && window.dockManager.openModal === this.modalSheet) {
        window.dockManager.openModal = null;
      }

      document.body.classList.remove('dock-open');
    }

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';

    const returnFocus = this.#modalReturnFocus;
    this.#modalReturnFocus = null;

    if (returnFocus && typeof returnFocus.focus === 'function') {
      returnFocus.focus();
    }
  }

  #openLightbox(src, alt) {
    if (!this.lightbox || !this.lightboxImg || !src) return;

    this.#lightboxReturnFocus = document.activeElement instanceof Element ? document.activeElement : null;
    this.lightboxImg.src = src;
    this.lightboxImg.alt = alt || '';
    this.lightbox.setAttribute('open', '');

    requestAnimationFrame(() => {
      this.lightbox.querySelector('.photo-stitch-lightbox__close')?.focus();
    });
  }

  #closeLightbox() {
    if (!this.lightbox || !this.lightbox.hasAttribute('open')) return;

    this.lightbox.removeAttribute('open');
    this.#lightboxJustClosed = true;

    if (this.lightboxImg) {
      this.lightboxImg.removeAttribute('src');
      this.lightboxImg.alt = '';
    }

    const returnFocus = this.#lightboxReturnFocus;
    this.#lightboxReturnFocus = null;

    if (returnFocus && typeof returnFocus.focus === 'function') {
      returnFocus.focus();
    }
  }

  #useDock() {
    return window.matchMedia('(width <= 74.99em)').matches;
  }

  #syncDockDismissed() {
    if (this.#isOpening) return;

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';

    if (window.dockManager && window.dockManager.openModal === this.modalSheet) {
      window.dockManager.openModal = null;
      document.body.classList.remove('dock-open');
    }
  }

  #onFileChange(input) {
    const key = input.dataset.corner;
    const file = input.files?.[0];

    if (!key || !file) return;

    const error = this.#validateFile(file);

    if (error) {
      this.#showError(error);
      input.value = '';
      return;
    }

    this.#hideError();
    this.#setCornerFile(key, file);
    this.validate();
    this.#updateSaveLabel();
  }

  #validateFile(file) {
    const accepted = this.config.acceptedTypes ?? [];
    const maxBytes = this.config.maxFileBytes ?? 10485760;

    if (accepted.length && !accepted.includes(file.type)) {
      return this.config.strings?.errorType ?? '';
    }

    if (file.size > maxBytes) {
      return this.config.strings?.errorSize ?? '';
    }

    return null;
  }

  #setCornerFile(key, file) {
    const existing = this.#uploads.get(key);

    if (existing?.url) {
      URL.revokeObjectURL(existing.url);
    }

    const url = URL.createObjectURL(file);
    const color = existing?.color || this.#defaultColorName() || '';
    this.#uploads.set(key, { file, url, color });
    this.#selectCorner(key);
    this.#renderCorner(key);
  }

  #clearCorner(key) {
    if (!key) return;

    const existing = this.#uploads.get(key);

    if (existing?.url) {
      URL.revokeObjectURL(existing.url);
    }

    this.#uploads.delete(key);

    const input = this.querySelector(`.js-ps-file[data-corner="${key}"]`);

    if (input) {
      input.value = '';
    }

    this.#renderCorner(key);

    if (this.#selectedCorner === key) {
      const nextKey = this.#uploads.keys().next().value ?? null;
      this.#selectCorner(nextKey);
    } else {
      this.#syncColorUi();
    }

    this.#hideError();
    this.validate();
    this.#updateSaveLabel();
  }

  #selectCorner(key) {
    this.#selectedCorner = key && this.#uploads.has(key) ? key : null;

    for (const root of this.querySelectorAll('.photo-stitch-corner')) {
      root.classList.toggle('photo-stitch-corner--selected', root.dataset.corner === this.#selectedCorner);
    }

    this.#syncColorUi();
  }

  #renderCorner(key) {
    const root = this.querySelector(`.photo-stitch-corner[data-corner="${key}"]`);

    if (!root) return;

    const upload = this.#uploads.get(key);
    const preview = root.querySelector('.js-ps-preview');
    const empty = root.querySelector('.photo-stitch-corner__empty');
    const clearBtn = root.querySelector('.js-ps-clear');
    const hit = root.querySelector('.js-ps-corner');
    const swatch = root.querySelector('.js-ps-corner-swatch');
    const corner = this.cornerByKey.get(key);
    const label = corner?.label ?? key;

    root.classList.toggle('photo-stitch-corner--selected', this.#selectedCorner === key);

    if (upload) {
      root.classList.add('photo-stitch-corner--filled');
      const hex = this.#colorHex(upload.color);
      root.style.setProperty('--photo-stitch-corner-colour', hex || 'transparent');

      if (preview) {
        preview.hidden = false;
        preview.style.backgroundImage = `url(${upload.url})`;
      }

      if (empty) {
        empty.hidden = true;
      }

      clearBtn?.classList.remove('hidden');

      if (clearBtn) {
        const removeTpl = this.config.strings?.cornerRemove ?? '';
        clearBtn.setAttribute('aria-label', removeTpl.replaceAll('__CORNER__', label));
      }

      if (swatch) {
        swatch.hidden = !hex;
        swatch.style.backgroundColor = hex || '';
        swatch.setAttribute('title', upload.color || '');
      }

      if (hit) {
        if (this.#selectedCorner === key) {
          const replaceTpl = this.config.strings?.cornerReplace ?? '';
          hit.setAttribute('aria-label', replaceTpl.replaceAll('__CORNER__', label));
        } else {
          const selectTpl = this.config.strings?.cornerSelect ?? '';
          hit.setAttribute('aria-label', selectTpl.replaceAll('__CORNER__', label));
        }
      }
    } else {
      root.classList.remove('photo-stitch-corner--filled');
      root.style.removeProperty('--photo-stitch-corner-colour');

      if (preview) {
        preview.hidden = true;
        preview.style.backgroundImage = '';
      }

      if (empty) {
        empty.hidden = false;
      }

      clearBtn?.classList.add('hidden');

      if (swatch) {
        swatch.hidden = true;
        swatch.style.backgroundColor = '';
        swatch.removeAttribute('title');
      }

      if (hit) {
        const addTpl = this.config.strings?.cornerAdd ?? '';
        hit.setAttribute('aria-label', addTpl.replaceAll('__CORNER__', label));
      }
    }
  }

  #showError(message) {
    if (!this.errorEl) return;

    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
  }

  #hideError() {
    if (!this.errorEl) return;

    this.errorEl.textContent = '';
    this.errorEl.classList.add('hidden');
  }

  #sanitizeNotesInput() {
    if (!this.notesInput) return;

    const cleaned = sanitizePhotoStitchNotes(this.notesInput.value);

    if (cleaned !== this.notesInput.value) {
      this.notesInput.value = cleaned;
    }
  }

  #getColorInputs() {
    return this.querySelectorAll('[name="photo-stitch-color"]');
  }

  #defaultColorName() {
    return this.config.defaultColorName || this.config.colors?.find((c) => c?.name)?.name || null;
  }

  #colorHex(name) {
    if (!name) return null;

    const match = this.config.colors?.find((c) => c?.name === name);
    return match?.hex || null;
  }

  #setCheckedColor(name) {
    const inputs = this.#getColorInputs();

    if (!inputs.length) return;

    const preferred = name || this.#defaultColorName();
    let matched = false;

    for (const input of inputs) {
      const isMatch = preferred != null && input.value === preferred;
      input.checked = isMatch;
      if (isMatch) matched = true;
    }

    if (!matched && inputs[0]) {
      inputs[0].checked = true;
    }
  }

  #applySelectedColor(colorName) {
    const key = this.#selectedCorner;

    if (!key) return;

    const upload = this.#uploads.get(key);

    if (!upload) return;

    upload.color = colorName || this.#defaultColorName() || '';
    this.#uploads.set(key, upload);
    this.#renderCorner(key);
    this.#syncColorUi();
  }

  #syncColorUi() {
    const selected = this.#selectedCorner ? this.#uploads.get(this.#selectedCorner) : null;
    const corner = this.#selectedCorner ? this.cornerByKey.get(this.#selectedCorner) : null;
    const hasSelection = Boolean(selected);

    if (this.colorGroup) {
      this.colorGroup.hidden = !hasSelection;
    }

    if (!hasSelection) {
      if (this.colorLabel) this.colorLabel.textContent = '';
      return;
    }

    this.#setCheckedColor(selected.color);

    const colorName =
      Array.from(this.#getColorInputs()).find((input) => input.checked)?.value ||
      selected.color ||
      this.#defaultColorName() ||
      '';

    if (selected.color !== colorName) {
      selected.color = colorName;
      this.#uploads.set(this.#selectedCorner, selected);
      this.#renderCorner(this.#selectedCorner);
    }

    if (this.colorHeading) {
      const tpl = this.config.strings?.colorFor ?? '';
      this.colorHeading.textContent = corner?.label
        ? tpl.replaceAll('__CORNER__', corner.label)
        : this.config.strings?.colorHint || '';
    }

    if (this.colorLabel) {
      this.colorLabel.textContent = colorName;
    }
  }

  #summaryColors() {
    const names = [];

    for (const entry of this.#uploads.values()) {
      if (!entry?.color) continue;
      if (!names.includes(entry.color)) names.push(entry.color);
    }

    return names.join(' · ');
  }

  #imageCount() {
    return this.#uploads.size;
  }

  /** First photo is free; additional photos each charge one fee unit. */
  #feeQuantity() {
    return Math.max(0, this.#imageCount() - 1);
  }

  #formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const format = this.config.moneyFormat || '{{amount}}';

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  #updateSaveLabel() {
    if (!this.saveBtn) return;

    const chargeable = this.#feeQuantity();
    const feeCents = Number(this.config.feePriceCents) || 0;
    const strings = this.config.strings ?? {};

    if (chargeable < 1 || feeCents <= 0) {
      this.saveBtn.textContent = strings.saveNoFee ?? '';
      return;
    }

    const total = this.#formatMoney(feeCents * chargeable);
    const tpl = strings.save ?? '';
    this.saveBtn.textContent = tpl.replaceAll('__PRICE__', `+${total}`);
  }

  validate() {
    const isValid = this.#imageCount() > 0;

    if (isValid) {
      this.saveBtn.removeAttribute('disabled');
    } else {
      this.saveBtn.setAttribute('disabled', 'true');
    }

    return isValid;
  }

  save() {
    this.#sanitizeNotesInput();

    if (!this.validate()) return;

    this.isActive = true;
    this.close();

    this.container.querySelector('.js-ps-state-inactive')?.classList.add('hidden');
    this.container.querySelector('.js-ps-state-active')?.classList.remove('hidden');

    const count = this.#imageCount();
    const colors = this.#summaryColors();
    const summaryTpl = this.config.strings?.summary ?? '';
    const summary = summaryTpl.replaceAll('__COUNT__', String(count)).replaceAll('__COLORS__', colors);

    if (this.statusText) {
      this.statusText.textContent = summary || this.config.strings?.statusAdded || '';
    }
  }

  reset() {
    this.isActive = false;
    this.#revokeAllObjectUrls();
    this.#uploads.clear();
    this.#selectedCorner = null;

    for (const input of this.querySelectorAll('.js-ps-file')) {
      input.value = '';
      this.#renderCorner(input.dataset.corner);
    }

    if (this.notesInput) {
      this.notesInput.value = '';
    }

    this.#syncColorUi();
    this.#hideError();
    this.#updateSaveLabel();
    this.validate();
    this.close();

    this.container.querySelector('.js-ps-state-inactive')?.classList.remove('hidden');
    this.container.querySelector('.js-ps-state-active')?.classList.add('hidden');
  }

  #revokeAllObjectUrls() {
    for (const entry of this.#uploads.values()) {
      if (entry?.url) {
        URL.revokeObjectURL(entry.url);
      }
    }
  }

  /**
   * Payload for ProductForm FormData + fee quantity branch.
   */
  getPayload() {
    if (!this.#initialized || !this.isActive) return null;

    const count = this.#imageCount();

    if (count < 1) return null;

    this.#sanitizeNotesInput();

    /** @type {Record<string, File>} */
    const files = {};
    const properties = {
      'For Product': this.config.productTitle,
      _unique_id: String(Date.now()),
    };

    for (const [key, entry] of this.#uploads.entries()) {
      const corner = this.cornerByKey.get(key);

      if (!corner?.property || !entry?.file) continue;

      files[corner.property] = entry.file;

      if (corner.colorProperty && entry.color) {
        properties[corner.colorProperty] = entry.color;
      }
    }

    const notes = this.notesInput ? sanitizePhotoStitchNotes(this.notesInput.value).trim() : '';

    if (notes) {
      properties.Notes = notes;
    }

    return {
      feeId: this.config.feeVariantId,
      feeQuantity: this.#feeQuantity(),
      files,
      properties,
    };
  }
}

customElements.define('product-photo-stitch', ProductPhotoStitch);
